import { inject } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { catchError, exhaustMap, forkJoin, map, of, switchMap } from 'rxjs';
import { StudentPortalService } from '../../../core/services/student-portal.service';
import { PreEnrollmentDocument } from '../models/pre-enrollment-documents.model';
import { PreEnrollmentDocumentsActions } from './pre-enrollment-documents.actions';

export class PreEnrollmentDocumentsEffects {
  private readonly actions$ = inject(Actions);
  private readonly studentPortalService = inject(StudentPortalService);

  readonly loadPageData$ = createEffect(() =>
    this.actions$.pipe(
      ofType(PreEnrollmentDocumentsActions.loadPageData),
      exhaustMap(() =>
        forkJoin({
          studentProfile: this.studentPortalService.getStudentProfileMe(),
          enrollments: this.studentPortalService.getEnrollments().pipe(catchError(() => of([]))),
        }).pipe(
          switchMap(({ studentProfile, enrollments }) => {
            const fallbackStudyPlanName =
              studentProfile.studyPlanName || enrollments[0]?.program || '';
            const studyPlanPublicId =
              studentProfile.studyPlanPublicId || enrollments[0]?.studyPlanPublicId || '';
            const studyPlanName$ = studyPlanPublicId
              ? this.studentPortalService.getStudyPlanName(studyPlanPublicId).pipe(
                  map((studyPlanName) => studyPlanName || fallbackStudyPlanName),
                  catchError(() => of(fallbackStudyPlanName)),
                )
              : of(fallbackStudyPlanName);
            const documents$ = this.studentPortalService.getMyStudentDocuments();

            return forkJoin({ studyPlanName: studyPlanName$, documents: documents$ });
          }),
          map(({ studyPlanName, documents }) =>
            PreEnrollmentDocumentsActions.loadPageDataSuccess({
              studyPlanName,
              documents: documents as PreEnrollmentDocument[],
            }),
          ),
          catchError(() =>
            of(
              PreEnrollmentDocumentsActions.loadPageDataFailure({
                error: 'No fue posible cargar los documentos del aspirante.',
              }),
            ),
          ),
        ),
      ),
    ),
  );

  readonly uploadDocument$ = createEffect(() =>
    this.actions$.pipe(
      ofType(PreEnrollmentDocumentsActions.uploadDocument),
      exhaustMap(({ document, file }) =>
        this.studentPortalService
          .uploadMyStudentDocument(document.documentTypePublicId || document.id, file)
          .pipe(
            map(() => PreEnrollmentDocumentsActions.refreshDocuments()),
            catchError(() =>
              of(
                PreEnrollmentDocumentsActions.uploadDocumentFailure({
                  document,
                  error: 'No fue posible subir el documento. Intente nuevamente.',
                }),
              ),
            ),
          ),
      ),
    ),
  );

  readonly refreshDocuments$ = createEffect(() =>
    this.actions$.pipe(
      ofType(PreEnrollmentDocumentsActions.refreshDocuments),
      switchMap(() =>
        this.studentPortalService.getMyStudentDocuments().pipe(
          map((documents) =>
            PreEnrollmentDocumentsActions.refreshDocumentsSuccess({
              documents: documents as PreEnrollmentDocument[],
            }),
          ),
          catchError(() =>
            of(
              PreEnrollmentDocumentsActions.refreshDocumentsFailure({
                error: 'El documento se subio, pero no fue posible actualizar la lista.',
              }),
            ),
          ),
        ),
      ),
    ),
  );
}
